import { CalendarClock } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getAllPurchaseTransactions } from "@/api/services/purchaseTransactionService";
import { InfoTooltip } from "@/components/tours/InfoTooltip";
import { Card, CardContent } from "@/components/ui/card";
import { HoverUnderlineSpan } from "@/components/ui/HoverUnderlineSpan";
import { useApp } from "@/contexts/AppContext";
import type { UpcomingPaymentData } from "@/hooks/useDrillDown";
import {
  DEMO_UPCOMING_COUNT,
  DEMO_UPCOMING_TOTAL,
  generateUpcomingPaymentsDrillDown,
  useDrillDown,
} from "@/hooks/useDrillDown";
import { useFormatting } from "@/hooks/useFormatting";
import { usePermissions } from "@/hooks/usePermissions";

export default function CeoUpcomingPaymentsCard() {
  const { hasPermission } = usePermissions();
  const { formatCurrency } = useFormatting();
  const { mode } = useApp();
  const { showDrillDown } = useDrillDown();

  // Live mode state
  const [liveUpcomingPayments, setLiveUpcomingPayments] = useState<
    UpcomingPaymentData[]
  >([]);
  const [livePaymentCount, setLivePaymentCount] = useState(0);
  const [livePaymentAmount, setLivePaymentAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const hasLoadedLiveData = useRef(false);

  useEffect(() => {
    if (mode !== "live") {
      hasLoadedLiveData.current = false;
      setLiveUpcomingPayments([]);
      setLivePaymentCount(0);
      setLivePaymentAmount(0);
      return;
    }

    if (hasLoadedLiveData.current) return;

    const loadLiveData = async () => {
      hasLoadedLiveData.current = true;
      setIsLoading(true);

      try {
        const purchaseResponse = await getAllPurchaseTransactions();
        const purchaseTransactions = purchaseResponse.purchaseTransactions;

        const now = new Date();
        const ninetyDaysFromNow = new Date(
          now.getTime() + 90 * 24 * 60 * 60 * 1000,
        );

        const upcomingPaymentsData: UpcomingPaymentData[] = purchaseTransactions
          .filter((txn) => {
            if ((txn.outstanding ?? 0) <= 0) return false;
            const effectiveDueDate =
              txn.dueDate || txn.anticipatedPaymentDate || txn.transactionDate;
            const dueDate = new Date(effectiveDueDate ?? "");
            return dueDate >= now && dueDate <= ninetyDaysFromNow;
          })
          .map((txn) => ({
            supplier: txn.accountName ?? "Unknown",
            description: txn.transactionDescription ?? null,
            dueDate:
              (txn.dueDate ||
                txn.anticipatedPaymentDate ||
                txn.transactionDate) ??
              "",
            outstandingValue: txn.outstanding ?? 0,
          }));

        setLiveUpcomingPayments(upcomingPaymentsData);
        setLivePaymentCount(upcomingPaymentsData.length);
        setLivePaymentAmount(
          upcomingPaymentsData.reduce((sum, p) => sum + p.outstandingValue, 0),
        );
      } catch (err) {
        console.error("Failed to load upcoming payments:", err);
      } finally {
        setIsLoading(false);
      }
    };

    loadLiveData();
  }, [mode]);

  if (!hasPermission("view_executive_cash_position_card")) return null;

  const displayCount = mode === "live" ? livePaymentCount : DEMO_UPCOMING_COUNT;
  const displayAmount =
    mode === "live" ? livePaymentAmount : DEMO_UPCOMING_TOTAL;

  const handleClick = () => {
    showDrillDown(
      "category-total",
      generateUpcomingPaymentsDrillDown(
        displayAmount,
        displayCount,
        mode === "live" ? liveUpcomingPayments : undefined,
      ),
    );
  };

  return (
    <Card
      className="h-full flex flex-col"
      style={{ borderLeft: "3px solid var(--mc-warning-border)" }}
    >
      <CardContent className="p-4 flex flex-col h-full">
        {/* Top row — title + icon badge */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-base font-medium text-neutral-800 truncate">
              Upcoming Payments
            </span>
            <InfoTooltip
              title="Upcoming Payments"
              content="Supplier invoices and bills due for payment in the next 30 days. Does not include recurring scheduled transactions."
            />
          </div>
          <div
            className="flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: "var(--mc-warning-bg)" }}
          >
            <CalendarClock
              className="w-5 h-5"
              style={{ color: "var(--mc-warning-icon)" }}
            />
          </div>
        </div>

        {/* Value */}
        <div className="mt-3">
          <HoverUnderlineSpan
            className="text-2xl font-bold text-neutral-900 tracking-tight cursor-pointer"
            onClick={handleClick}
          >
            {isLoading ? "\u2026" : displayCount}
          </HoverUnderlineSpan>
        </div>

        {/* Sub-text */}
        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-xs font-semibold text-neutral-800">
            {formatCurrency(displayAmount, "full")}
          </span>
          <span className="text-xs font-normal text-neutral-800">
            due in 30 days
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
